import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

/**
 * Helper function to convert 24-hour time to 12-hour format with AM/PM
 * @param hours - Hours in 24-hour format (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Formatted time string in 12-hour format (HH:MM AM/PM)
 */
const formatTime12Hour = (hours: number, minutes: number): string => {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
};

/**
 * Helper function to convert UTC datetime string to local time
 * @param dateString - ISO date string or datetime string
 * @returns Formatted local datetime string
 */
export const convertUTCToLocal = (dateString: string): string => {
    try {
        // Check if it's an ISO date string
        if (dateString.includes('T') || dateString.includes('Z') || dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                // Format as MM/DD/YYYY 12H AM/PM for datetime
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                const year = date.getFullYear();
                const hours = date.getHours();
                const minutes = date.getMinutes();

                // Format time as 12H AM/PM
                const timeStr = formatTime12Hour(hours, minutes);
                return `${month}/${day}/${year} ${timeStr}`;
            }
        }
        return dateString;
    } catch (error) {
        console.error('Error converting UTC to local time:', error);
        return dateString;
    }
};


/**
 * Helper function to format datetime labels with UTC to local conversion
 * @param label - The datetime label string
 * @param questionType - The question type (date, time, datetime)
 * @returns Formatted datetime string
 */
export const formatDateTimeLabel = (label: string, questionType: string): string => {
    if (!['date', 'time', 'datetime'].includes(questionType)) {
        return label;
    }

    // Handle time type: convert from UTC to local time and format as 12H AM/PM
    if (questionType === 'time') {
        try {
            // Check if it's a time string like HH:mm (assume UTC)
            const timeMatch = label.match(/^(\d{1,2}):(\d{2})$/);
            if (timeMatch) {
                const hours = parseInt(timeMatch[1]);
                const minutes = parseInt(timeMatch[2]);

                // Create a UTC date for the time
                const utcDate = new Date();
                utcDate.setUTCHours(hours, minutes, 0, 0);

                // Convert to local time
                const localHours = utcDate.getHours();
                const localMinutes = utcDate.getMinutes();

                return formatTime12Hour(localHours, localMinutes);
            }

            // If it's an ISO datetime string, extract time and convert
            if (label.includes('T') || label.includes('Z') || label.match(/^\d{4}-\d{2}-\d{2}/)) {
                const date = new Date(label);
                if (!isNaN(date.getTime())) {
                    const localHours = date.getHours();
                    const localMinutes = date.getMinutes();
                    return formatTime12Hour(localHours, localMinutes);
                }
            }
        } catch (error) {
            console.error('Error converting time from UTC to local:', error);
        }
        return label;
    }

    // Handle date type: convert to MM/DD/YYYY format
    if (questionType === 'date') {
        try {
            // If it's already in YYYY-MM-DD format, convert to MM/DD/YYYY
            if (label.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = label.split('-');
                return `${month}/${day}/${year}`;
            }
            // Try to parse and format as MM/DD/YYYY
            const date = new Date(label);
            if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                return `${month}/${day}/${year}`;
            }
        } catch (error) {
            console.error('Error converting date format:', error);
        }
        return label;
    }

    // Handle datetime type: keep original behavior
    // Handle different date/time formats
    if (label.includes('time:')) {
        // Extract time value and convert to date if possible
        const timeMatch = label.match(/time:(\d{2}):(\d{2})/);
        if (timeMatch) {
            const hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            // If it's a time that represents a date (like 24:00 meaning end of day)
            if (hours === 24 && minutes === 0) {
                return '12/31/2023'; // Default end date format
            }
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    }


    // Handle date formats
    if (label.includes('date:')) {
        const dateMatch = label.match(/date:(.+)/);
        if (dateMatch) {
            const dateStr = dateMatch[1];
            // Handle dd/mm/yy format (2-digit year) - more flexible matching
            const ddMmYyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{1,2})$/);
            if (ddMmYyMatch) {
                const day = ddMmYyMatch[1].padStart(2, '0');
                const month = ddMmYyMatch[2].padStart(2, '0');
                const year = ddMmYyMatch[3];

                // Convert 2-digit year to 4-digit year
                // Assume years 00-30 are 2000-2030, years 31-99 are 1931-1999
                const fullYear = parseInt(year) <= 30 ? 2000 + parseInt(year) : 1900 + parseInt(year);

                return `${month}/${day}/${fullYear}`;
            }

            // Handle other date formats
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
                return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
            }
        }
    }

    // Handle datetime type: convert from UTC to local time and format as MM/DD/YYYY 12H AM/PM
    if (questionType === 'datetime') {
        try {
            // Try to parse as ISO date string and convert UTC to local
            if (label.includes('-') || label.includes('T') || label.includes('Z')) {
                const date = new Date(label);
                if (!isNaN(date.getTime())) {
                    // Convert to local time
                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                    const day = date.getDate().toString().padStart(2, '0');
                    const year = date.getFullYear();
                    const hours = date.getHours();
                    const minutes = date.getMinutes();

                    // Format time as 12H AM/PM
                    const timeStr = formatTime12Hour(hours, minutes);
                    return `${month}/${day}/${year} ${timeStr}`;
                }
            }

            // Try to parse as regular date string
            const date = new Date(label);
            if (!isNaN(date.getTime())) {
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                const year = date.getFullYear();
                const hours = date.getHours();
                const minutes = date.getMinutes();

                // Format time as 12H AM/PM
                const timeStr = formatTime12Hour(hours, minutes);
                return `${month}/${day}/${year} ${timeStr}`;
            }
        } catch (error) {
            console.error('Error converting datetime from UTC to local:', error);
        }
    }

    // Try to parse as ISO date string and convert UTC to local (for other types)
    if (label.includes('-') || label.includes('T') || label.includes('Z')) {
        return convertUTCToLocal(label);
    }

    // Try to parse as regular date string
    const date = new Date(label);
    if (!isNaN(date.getTime())) {
        if (questionType === 'date') {
            // For date only - format as MM/DD/YYYY
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        }
    }

    return label;
};

export function formatToTimeString(timeStr: string | Date): string {
    if (!timeStr) return "";
    if (dayjs(timeStr, "HH:mm:ss", true).isValid()) return timeStr as string;
    return dayjs(timeStr).format("HH:mm:ss");
};

export function getLocalDayRange(dateStr: string): { start: Date; end: Date } {
    const date = new Date(dateStr); // ví dụ: '2025-10-25'

    const start = new Date(date);
    start.setHours(0, 0, 0, 0); // 00:00:00.000 local time

    const end = new Date(date);
    end.setHours(23, 59, 59, 999); // 23:59:59.999 local time

    return { start, end };
}

// "H:MM AM/PM" if time < 10, "HH:MM AM/PM" if >= 10
export function formatCompactTime(dateInput: string | number | Date): string {
    if (!dateInput) return '';
    return dayjs(dateInput).format('h:mm A');
}

export function formatDate(dateStr: string, fromFormat: string, toFormat: string): string | null {
    if (!dateStr) return null;

    const parsed = dayjs(dateStr, fromFormat, true); // strict mode

    if (!parsed.isValid()) return null;

    return parsed.format(toFormat);
}

export const getTimeRangeFilter = (timeFilter: string, fromDate?: Date, toDate?: Date) => {
    switch (timeFilter) {
        case "Today": 
        case "today": {
            const from = new Date();
            from.setHours(0, 0, 0, 0);

            const to = new Date();
            to.setHours(23, 59, 59, 999);

            return { from: from.toISOString(), to: to.toISOString() };
        }

        case "This week":
        case "this_week": {
            const now = new Date();
            const day = now.getDay(); // 0 = Sunday
            const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day

            const from = new Date(now.setDate(diff));
            from.setHours(0, 0, 0, 0);

            const to = new Date(from);
            to.setDate(from.getDate() + 6);
            to.setHours(23, 59, 59, 999);

            return { from: from.toISOString(), to: to.toISOString() };
        }

        case "This Month":
        case "this_month": {
            const now = new Date();

            const from = new Date(now.getFullYear(), now.getMonth(), 1);
            from.setHours(0, 0, 0, 0);

            const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            to.setHours(23, 59, 59, 999);

            return { from: from.toISOString(), to: to.toISOString() };
        }

        case "Last Year":
        case "last_year": {
            const year = new Date().getFullYear() - 1;

            const from = new Date(year, 0, 1);
            from.setHours(0, 0, 0, 0);

            const to = new Date(year, 11, 31);
            to.setHours(23, 59, 59, 999);

            return { from: from.toISOString(), to: to.toISOString() };
        }

        case "Custom Range":
        case "custom_range": {
            const from = fromDate ? new Date(fromDate) : null;
            const to = toDate ? new Date(toDate) : null;

            if (from) {
                from.setHours(0, 0, 0, 0);
            }

            if (to) {
                to.setHours(23, 59, 59, 999);
            }

            return {
                from: from ? from.toISOString() : null,
                to: to ? to.toISOString() : null
            };
        }

        default:
            return null;
    }

};

/**
 * Convert a date string (YYYY-MM-DD) to UTC datetime range for API filtering
 * This ensures proper comparison with database timestamps that include time
 * 
 * @param dateString - Date string in YYYY-MM-DD format
 * @param isStartOfDay - If true, returns start of day (00:00:00), otherwise end of day (23:59:59)
 * @returns ISO string in UTC timezone
 * 
 * @example
 * // Client timezone: UTC+7
 * // Input: "2025-11-25", isStartOfDay: true
 * // Output: "2024-11-24T17:00:00.000Z" (which is 2025-11-25 00:00:00 in UTC+7)
 * 
 * // Input: "2025-11-25", isStartOfDay: false
 * // Output: "2024-11-25T16:59:59.999Z" (which is 2025-11-25 23:59:59 in UTC+7)
 */
export function convertDateToUTCRange(dateString: string, isStartOfDay: boolean): string {
    if (!dateString) return '';
    
    // Parse the date string as local date
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
        console.error('Invalid date string:', dateString);
        return '';
    }
    
    if (isStartOfDay) {
        // Set to start of day in local timezone (00:00:00.000)
        date.setHours(0, 0, 0, 0);
    } else {
        // Set to end of day in local timezone (23:59:59.999)
        date.setHours(23, 59, 59, 999);
    }
    
    // Convert to UTC ISO string
    // JavaScript automatically handles timezone conversion
    return date.toISOString();
}

export function ensure2230Z(input: string): string {
  let d = dayjs(input);

  // if only include date (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    d = dayjs.utc(`${input}T12:00:00Z`);
  } else {
    // if include date and time (YYYY-MM-DDTHH:mm:ssZ)
    const dateStr = d.utc().format("YYYY-MM-DD");
    d = dayjs.utc(`${dateStr}T12:00:00Z`);
  }

  return d.toISOString(); // -> luôn trả về dạng UTC chuẩn
}
